import React from 'react';
import { motion } from 'framer-motion';
import {
  FiUsers,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiCalendar,
  FiShield,
  FiUser,
  FiFileText,
  FiPlus,
  FiActivity,
  FiLogIn,
  FiLogOut
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Table } from 'antd';
import { apiUrl, apiFetch } from '../api';
import { getVisitStatusBadgeClass, getVisitStatusLabel } from '../utils/visitStatus';
import { isEmployeeRole } from '../utils/companyRoles';
import { getLocalDateString } from '../utils/date';
import { cleanUserName } from '../utils/nameHelper';
import BannerBg from '../assets/Banner.png';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ArcElement
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [chartType, setChartType] = React.useState('bar');
  const [insightMode, setInsightMode] = React.useState('overview');
  const [trafficTimeframe, setTrafficTimeframe] = React.useState('yearly');
  const [activityTab, setActivityTab] = React.useState('pending');
  const [preApprovedVisitors, setPreApprovedVisitors] = React.useState([]);
  const [allVisits, setAllVisits] = React.useState([]);
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [previewPhoto, setPreviewPhoto] = React.useState('');
  const [previewName, setPreviewName] = React.useState('');
  const [stats, setStats] = React.useState({
    totalEmployees: 0,
    activeVisitors: 0,
    pendingRequests: 0,
    totalCheckIns: 0,
    recentVisits: []
  });
  const [insights, setInsights] = React.useState({
    todaysExpected: 0,
    checkedInToday: 0,
    approvedWaiting: 0,
    checkedOutToday: 0
  });
  const [chartLabels, setChartLabels] = React.useState(['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May']);
  const [chartDataValues, setChartDataValues] = React.useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [loading, setLoading] = React.useState(true);

  // Stat Modal State
  const [statModal, setStatModal] = React.useState({
    isOpen: false,
    title: '',
    endpoint: '',
    type: 'visit'
  });
  const [statModalData, setStatModalData] = React.useState([]);
  const [statModalLoading, setStatModalLoading] = React.useState(false);
  const [statModalPage, setStatModalPage] = React.useState(1);
  const [statModalTotal, setStatModalTotal] = React.useState(0);

  const openStatModal = (title, endpoint, type = 'visit') => {
    setStatModal({ isOpen: true, title, endpoint, type });
    setStatModalPage(1);
  };

  React.useEffect(() => {
    if (statModal.isOpen) {
      const fetchStatData = async () => {
        setStatModalLoading(true);
        try {
          const res = await apiFetch(`${statModal.endpoint}${statModal.endpoint.includes('?') ? '&' : '?'}page=${statModalPage}&limit=10`);
          const data = await res.json();
          setStatModalData(data.data || []);
          setStatModalTotal(data.totalDocs || 0);
        } catch (error) {
          console.error('Error fetching stat data', error);
        }
        setStatModalLoading(false);
      };
      fetchStatData();
    }
  }, [statModal.isOpen, statModal.endpoint, statModalPage]);

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const user = JSON.parse(localStorage.getItem('user') || '{"name":"Admin User", "email": "admin@vms.com", "mobile": "+91 9045324564", "role": "Admin"}');
  const isEmployee = isEmployeeRole(user.role);
  const canCheckInOut = user.role?.toLowerCase() === 'receptionist';

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const loadDashboardData = async () => {
    try {
      const todayStr = getLocalDateString();
      const [statsRes, visitRes] = await Promise.all([
        apiFetch(`/api/visits/stats?timeframe=${trafficTimeframe}`),
        apiFetch(`/api/visits?date=${todayStr}&limit=100`)
      ]);

      const statsData = await statsRes.json();
      const visitData = await visitRes.json();

      if (statsRes.ok && visitRes.ok) {
        const s = statsData.data;
        setStats({
          totalEmployees: s.totalEmployees || 0,
          activeVisitors: s.checkedInToday || 0,
          pendingRequests: s.pendingToday || 0,
          totalCheckIns: (s.checkedInToday || 0) + (s.checkedOutToday || 0),
          recentVisits: (visitData.data || []).slice(0, 5)
        });

        setInsights({
          todaysExpected: s.todaysExpected || 0,
          checkedInToday: s.checkedInToday || 0,
          approvedWaiting: s.approvedWaiting || 0,
          checkedOutToday: s.checkedOutToday || 0
        });

        if (s.chartLabels && s.chartDataValues) {
          setChartLabels(s.chartLabels);
          setChartDataValues(s.chartDataValues);
        }

        const visits = Array.isArray(visitData.data) ? visitData.data : [];
        setAllVisits(visits);

        // Pre-approved today (for list)
        const approved = visits.filter((visit) => {
          const visitDateStr = getLocalDateString(visit.expectedDate || visit.createdAt);
          return (visit.status === 'approved' || visit.status === 'checked-in') && visitDateStr === todayStr;
        });
        setPreApprovedVisitors(approved.slice(0, 10));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const res = await apiFetch(`/api/visits/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  React.useEffect(() => {
    loadDashboardData();
  }, [trafficTimeframe]);

  const todayStrForFilter = getLocalDateString();
  const todaysExpected = insights.todaysExpected || 0;
  const checkedInToday = insights.checkedInToday || 0;
  const checkedOutToday = insights.checkedOutToday || 0;
  const approvedWaiting = insights.approvedWaiting || 0;
  const todayTotalCheckIns = checkedInToday + checkedOutToday;

  const progressDenominator = todayTotalCheckIns + todaysExpected;
  const visitProgressRaw = progressDenominator > 0 ? Math.round((todayTotalCheckIns / progressDenominator) * 100) : 0;
  const visitProgress = visitProgressRaw > 100 ? 100 : visitProgressRaw;

  // Total Employees (the directory count) is only meaningful to roles that can
  // view the Employees directory — admin / superadmin. Others don't see the card.
  const canViewEmployees = ['admin', 'superadmin'].includes(user.role?.toLowerCase());

  const dashboardStats = [
    ...(canViewEmployees ? [{ title: 'Total Employees', value: stats.totalEmployees, icon: <FiUsers />, note: 'Directory', action: () => openStatModal('Total Employees', '/api/employees', 'employee') }] : []),
    { title: 'Active Visitors', value: stats.activeVisitors, icon: <FiClock />, note: 'On premises', action: () => openStatModal('Active Visitors', '/api/visits?status=checked-in', 'visit') },
    { title: 'Pending Requests', value: stats.pendingRequests, icon: <FiAlertTriangle />, note: 'Needs review', action: () => openStatModal('Pending Requests', '/api/visits?status=pending', 'visit') },
    { title: 'Total Check-ins', value: todayTotalCheckIns, icon: <FiCheckCircle />, note: 'Today\'s visits', action: () => openStatModal('Total Check-ins', `/api/visits?status=checked-in,checked-out&date=${todayStrForFilter}`, 'visit') },
  ];

  const cleanedName = cleanUserName(user.name, user.companyName);
  const firstName = cleanedName.split(' ')[0] || 'User';
  const displayEmail = user.email || 'admin@vms.com';
  const displayMobile = user.mobile || user.phoneNumber || '0000000000';
  const displayDate = currentTime.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const displayTime = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const todaysVisits = allVisits.filter(visit => {
    const visitDateStr = getLocalDateString(visit.expectedDate || visit.createdAt);
    return visitDateStr === todayStrForFilter;
  });

  const pendingVisits = todaysVisits.filter((visit) => visit.status === 'pending');
  const actualPreApprovedVisitors = todaysVisits.filter((visit) => visit.status === 'approved' && visit.preApproved);
  const recentVisits = todaysVisits.length > 0 ? todaysVisits.slice(0, 5) : [];
  const activeActivityItems = activityTab === 'pre-approved'
    ? actualPreApprovedVisitors
    : activityTab === 'pending'
      ? pendingVisits
      : recentVisits;

  const formatVisitTime = (value) => {
    if (!value) return '--:--';
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatVisitDate = (value) => {
    if (!value) return 'Today';
    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const openPhotoPreview = (photo, name) => {
    if (!photo) return;
    setPreviewPhoto(photo);
    setPreviewName(name);
  };

  const chartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Visitors',
      data: chartDataValues,
      backgroundColor: '#0075bd',
      borderColor: '#0075bd',
      borderRadius: 6,
      barPercentage: 0.58,
      categoryPercentage: 0.58,
      fill: true,
      tension: 0.35,
      pointBackgroundColor: '#0075bd',
      pointRadius: 3,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, grid: { color: '#e5e7eb' }, ticks: { color: '#8d93a5' } },
      x: { grid: { color: '#eef2f7' }, ticks: { color: '#8d93a5' } }
    }
  };

  const pending = stats.pendingRequests;
  const notArrived = Math.max(0, todaysExpected - checkedInToday - actualPreApprovedVisitors.length - pending - insights.checkedOutToday);
  const arrived = Math.max(0, Math.min(todaysExpected, todaysExpected - notArrived));

  const bucketLabels = ['8AM', '9AM', '10AM', '11AM', '12PM', '1PM', '2PM', '3PM', '4PM', '5PM'];
  const arrivalsArray = new Array(10).fill(0);
  const checkInsArray = new Array(10).fill(0);
  const checkOutsArray = new Array(10).fill(0);

  todaysVisits.forEach(visit => {
    let arrivalDate = new Date(visit.expectedDate || visit.createdAt);
    if (visit.time) {
      const match = visit.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (match) {
        let hour = parseInt(match[1]);
        const min = parseInt(match[2]);
        const ampm = match[3].toUpperCase();
        if (ampm === 'PM' && hour < 12) hour += 12;
        if (ampm === 'AM' && hour === 12) hour = 0;
        arrivalDate.setHours(hour, min, 0, 0);
      }
    }

    const h = arrivalDate.getHours();
    if (h >= 8 && h <= 17) {
      arrivalsArray[h - 8]++;
    }

    if (visit.checkInTime) {
      const ciDate = new Date(visit.checkInTime);
      const cih = ciDate.getHours();
      if (cih >= 8 && cih <= 17) checkInsArray[cih - 8]++;
    }

    if (visit.checkOutTime) {
      const coDate = new Date(visit.checkOutTime);
      const coh = coDate.getHours();
      if (coh >= 8 && coh <= 17) checkOutsArray[coh - 8]++;
    }
  });

  const cumulativeArrivals = [];
  let sum = 0;
  for (let i = 0; i < arrivalsArray.length; i++) {
    sum += arrivalsArray[i];
    cumulativeArrivals.push(sum);
  }

  const donutDataRaw = [pending, actualPreApprovedVisitors.length, notArrived, checkedInToday, insights.checkedOutToday];
  const donutLabelsRaw = ['Pending', 'Pre-approved', 'Not Arrived', 'Checked In', 'Checked Out'];
  const donutColorsRaw = ['#993556', '#BA7517', '#B5D4F4', '#1D9E75', '#888780'];

  const donutLabels = [];
  const donutDataValues = [];
  const donutColors = [];
  donutDataRaw.forEach((val, i) => {
    if (val > 0) {
      donutLabels.push(donutLabelsRaw[i]);
      donutDataValues.push(val);
      donutColors.push(donutColorsRaw[i]);
    }
  });

  const isDonutEmpty = donutDataValues.length === 0;

  const donutChartData = {
    labels: isDonutEmpty ? ['No visitor data today'] : donutLabels,
    datasets: [{
      data: isDonutEmpty ? [1] : donutDataValues,
      backgroundColor: isDonutEmpty ? ['#e2e8f0'] : donutColors,
      borderWidth: 0,
      hoverOffset: isDonutEmpty ? 0 : 4
    }]
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { enabled: !isDonutEmpty }
    }
  };

  const maxSeriesValue = Math.max(...arrivalsArray, ...checkInsArray, ...checkOutsArray, 0);
  const barMaxY = Math.max(4, maxSeriesValue + 1);
  const barStepSize = maxSeriesValue <= 5 ? 1 : 2;

  const hourlyBarData = {
    labels: bucketLabels,
    datasets: [
      { label: 'Arrivals', data: arrivalsArray, backgroundColor: '#378ADD', borderRadius: 4 },
      { label: 'Check-ins', data: checkInsArray, backgroundColor: '#1D9E75', borderRadius: 4 },
      { label: 'Check-outs', data: checkOutsArray, backgroundColor: '#888780', borderRadius: 4 }
    ]
  };

  const hourlyBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: {
      y: { beginAtZero: true, max: barMaxY, ticks: { stepSize: barStepSize } }
    }
  };

  const lineMaxCumulative = Math.max(...cumulativeArrivals, 0);
  const targetExpected = Math.max(todaysExpected, 1);
  const lineMaxY = Math.max(targetExpected, lineMaxCumulative) + 2;
  const lineStepSize = lineMaxY > 12 ? 2 : 1;

  const hourlyLineData = {
    labels: bucketLabels,
    datasets: [
      {
        label: 'Cumulative Arrivals',
        data: cumulativeArrivals,
        borderColor: '#378ADD',
        backgroundColor: '#378ADD',
        tension: 0.3,
        fill: false,
        pointRadius: 4,
      },
      {
        label: 'Target',
        data: new Array(10).fill(todaysExpected),
        borderColor: '#E24B4A',
        borderDash: [5, 5],
        pointRadius: 0,
        fill: false,
      }
    ]
  };

  const hourlyLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: {
      y: { beginAtZero: true, max: lineMaxY, ticks: { stepSize: lineStepSize } }
    }
  };

  const FunnelChart = () => {
    const denom = Math.max(todaysExpected, 1);
    const stages = [
      { label: 'Expected', value: todaysExpected, pct: 1.0, color: '#378ADD' },
      { label: 'Arrived', value: arrived, pct: arrived / denom, color: '#185FA5' },
      { label: 'Checked In', value: checkedInToday, pct: checkedInToday / denom, color: '#0F6E56' },
      { label: 'Checked Out', value: insights.checkedOutToday, pct: insights.checkedOutToday / denom, color: '#444441' }
    ];

    return (
      <div className="flex flex-col gap-3 py-4 w-full px-2 h-full justify-center">
        {stages.map((s, i) => (
          <div key={i} className="flex items-center gap-4 w-full">
            <div className="w-24 text-right text-xs font-medium text-slate-600 dark:text-slate-300">{s.label}</div>
            <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-800 rounded relative flex items-center overflow-hidden">
              <div
                className="h-full rounded flex items-center px-3 transition-all duration-500 min-w-[36px]"
                style={{ width: `${Math.max(s.pct * 100, 5)}%`, backgroundColor: s.color }}
              >
                <span className="text-white text-xs font-semibold">{s.value}</span>
              </div>
            </div>
            <div className="w-12 text-sm font-semibold text-slate-700 dark:text-slate-200">{Math.round(s.pct * 100)}%</div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="vms-dashboard is-loading select-none pointer-events-none">
        <section className="vms-dashboard-main">
          <div className="vms-overview-row">
            <div className="vms-loading-block vms-greeting-card"></div>
            <div className="vms-loading-block vms-summary-card"></div>
          </div>
          <div className="vms-stat-grid">
            {[...Array(4)].map((_, idx) => <div key={idx} className="vms-loading-block vms-stat-card"></div>)}
          </div>
          <div className="vms-loading-block vms-panel min-h-[320px]"></div>
        </section>
        <aside className="vms-loading-block vms-activity-board"></aside>
      </div>
    );
  }

  return (
    <div className="vms-dashboard">
      <section className="vms-dashboard-main">
        <div className="vms-overview-row">
          <div
            className="vms-greeting-card"
            style={{
              backgroundImage: `url(${BannerBg})`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="vms-hero-content">
              <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                {getGreeting()}, {firstName}!
              </motion.h1>
              <p className="vms-hero-contact">
                {displayEmail} <span>|</span> {displayMobile}
              </p>
              <div className="vms-hero-meta">
                <span><FiUser /> {cleanedName || 'Admin User'}</span>
                <span><FiCalendar /> {displayDate}</span>
              </div>
            </div>
          </div>

          <div className="vms-summary-card">
            <div>
              <p className="vms-kicker">Today: Visitor Desk</p>
              <h2>{displayTime}</h2>
            </div>
            <div className="vms-summary-split">
              <div>
                <span>Total Expected Visitors</span>
                <strong className="text-emerald-600">{todaysExpected}</strong>
              </div>
              <div>
                <span>Total Check-ins</span>
                <strong>{todayTotalCheckIns}</strong>
              </div>
            </div>
            <div className="vms-progress-row">
              <div>
                <span>Visit Progress</span>
                <strong>{visitProgress}%</strong>
              </div>
              <div className="vms-progress-track">
                <span style={{ width: `${visitProgress}%` }}></span>
              </div>
            </div>
          </div>
        </div>

        <div className="vms-action-row">
          <Button icon={<FiCalendar />} onClick={() => navigate('/visit-requests')} className="vms-outline-btn">
            Visit Requests
          </Button>
          <Button icon={<FiFileText />} onClick={() => navigate('/logs')} className="vms-outline-btn">
            Visitor Activity
          </Button>
          <Button type="primary" icon={<FiPlus />} onClick={() => navigate('/register')} className="vms-solid-btn">
            Register Visitor
          </Button>
        </div>

        <div className="vms-stat-grid">
          {dashboardStats.map((stat, idx) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.04 }}
              className="vms-stat-card cursor-pointer hover:shadow-md transition-shadow"
              onClick={stat.action}
            >
              <div className="flex justify-between items-start mb-3">
                <p className="text-slate-600 font-medium text-[15px] m-0">{stat.title}</p>
                <div className="text-[#0073ba]">{React.cloneElement(stat.icon, { size: 20 })}</div>
              </div>
              <div className="flex flex-col gap-1">
                <strong className="text-[#006da8] text-2xl font-bold leading-none">{stat.value}</strong>
                <span className="text-slate-400 text-xs">{stat.note}</span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="vms-insight-grid">
          <section className="vms-panel overflow-hidden flex flex-col">
            <div className="vms-panel-header mb-2 flex-wrap gap-3">
              <div>
                <h2>Today's Visitor Insights</h2>
                <p>{currentTime.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              </div>
              <div className="flex items-center">
                <select
                  value={insightMode}
                  onChange={(e) => setInsightMode(e.target.value)}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="overview">Overview</option>
                  <option value="donut">Donut Chart</option>
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                </select>
              </div>
            </div>

            <div className="flex-1 flex flex-col">
              {insightMode === 'overview' && (
                <>
                  <div className="vms-total-strip mt-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" onClick={() => openStatModal('Total Expected Visitors', `/api/visits?date=${todayStrForFilter}&status=pending,approved,pre-approved`, 'visit')}>
                    <div>
                      <p>Total Expected Visitors</p>
                      <strong>{todaysExpected}</strong>
                    </div>
                    <FiUsers />
                  </div>
                  <div className="vms-mini-grid mt-4">
                    <div className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded p-2" onClick={() => openStatModal('Checked In Today', `/api/visits?date=${todayStrForFilter}&status=checked-in`, 'visit')}>
                      <p>Checked In</p><strong>{checkedInToday}</strong><FiLogIn />
                    </div>
                    <div className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded p-2" onClick={() => openStatModal('Pre-approved Today', `/api/visits?date=${todayStrForFilter}&status=pre-approved`, 'visit')}>
                      <p>Pre-approved</p><strong>{actualPreApprovedVisitors.length}</strong><FiShield />
                    </div>
                    <div className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded p-2" onClick={() => openStatModal('Pending Today', `/api/visits?date=${todayStrForFilter}&status=pending`, 'visit')}>
                      <p>Pending</p><strong>{stats.pendingRequests}</strong><FiAlertTriangle />
                    </div>
                    <div className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded p-2" onClick={() => openStatModal('Checked Out Today', `/api/visits?date=${todayStrForFilter}&status=checked-out`, 'visit')}>
                      <p>Checked Out</p><strong>{insights.checkedOutToday}</strong><FiLogOut />
                    </div>
                  </div>
                </>
              )}

              {insightMode === 'donut' && (
                <div className="relative w-full h-[260px] flex items-center justify-center mt-2">
                  <Doughnut data={donutChartData} options={donutOptions} />
                  {!isDonutEmpty && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-6">
                      <span className="text-3xl font-bold text-slate-800 dark:text-slate-100 leading-none">{todaysExpected}</span>
                      <span className="text-xs text-slate-500 uppercase font-semibold mt-1">Visitors</span>
                    </div>
                  )}
                </div>
              )}

              {insightMode === 'bar' && (
                <div className="w-full h-[260px] mt-2">
                  <Bar data={hourlyBarData} options={hourlyBarOptions} />
                </div>
              )}

              {insightMode === 'line' && (
                <div className="w-full h-[260px] mt-2">
                  <Line data={hourlyLineData} options={hourlyLineOptions} />
                </div>
              )}
            </div>
          </section>

          <section className="vms-panel vms-chart-panel flex flex-col">
            <div className="vms-panel-header">
              <div>
                <h2>Visitor Traffic</h2>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={trafficTimeframe}
                  onChange={(e) => setTrafficTimeframe(e.target.value)}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="all-time">All Time</option>
                </select>
                <div className="vms-chart-toggle">
                  <button type="button" onClick={() => setChartType('bar')} className={chartType === 'bar' ? 'active' : ''}>
                    Bar
                  </button>
                  <button type="button" onClick={() => setChartType('line')} className={chartType === 'line' ? 'active' : ''}>
                    Line
                  </button>
                </div>
              </div>
            </div>
            <div className="vms-chart-canvas">
              {chartType === 'bar' ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </section>
        </div>
      </section>

      <aside className="vms-activity-board">
        <div className="vms-activity-header">
          <div>
            <FiShield />
            <h2>Visit Activity</h2>
          </div>
          <button type="button" onClick={() => navigate('/visit-requests')}>View all</button>
        </div>
        <div className="vms-activity-tabs flex overflow-x-auto gap-2 custom-scrollbar pb-2 snap-x">
          {[
            ['pending', 'Pending'],
            ['pre-approved', 'Pre-Approved'],
            ['recent', 'Recent']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActivityTab(key)}
              className={`${activityTab === key ? 'is-active' : ''} flex-shrink-0 snap-start`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="vms-activity-list overflow-y-auto custom-scrollbar pr-2">
          {activeActivityItems.length === 0 && (
            <div className="vms-empty-state">
              <FiActivity />
              <p>No {activityTab} visitor activity yet.</p>
            </div>
          )}


          {activityTab === 'pre-approved' && actualPreApprovedVisitors.map((record) => {
            const visitorName = `${record.visitor?.firstName || 'Unknown'} ${record.visitor?.lastName || 'Visitor'}`;
            const visitDateStr = getLocalDateString(record.expectedDate || record.createdAt);
            return (
              <article key={`pre-approved-${record._id}`} className="vms-activity-card">
                <div className="vms-activity-card-top">
                  <h3 className="flex items-center gap-2">
                    {record.visitor?.photo ? (
                      <button
                        type="button"
                        onClick={() => openPhotoPreview(record.visitor.photo, visitorName)}
                        className="w-7 h-7 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700"
                        title="View visitor photo"
                      >
                        <img src={record.visitor.photo} alt="Visitor" className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-slate-500">
                        {record.visitor?.firstName?.charAt(0) || 'V'}
                      </span>
                    )}
                    {visitorName}
                  </h3>
                  <span className="pre-approved">pre-approved</span>
                </div>
                <p>{record.purpose || 'General visit'} request for {formatVisitDate(record.expectedDate || record.createdAt)}.</p>
                <div className="vms-activity-meta">
                  <span><FiClock /> {formatVisitTime(record.expectedDate || record.createdAt)}</span>
                  <strong>{record.host ? `${record.host.firstName} ${record.host.lastName || ''}`.trim() : 'Host'}</strong>
                </div>
                {canCheckInOut && (
                  <div className="vms-card-actions">
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        if (visitDateStr !== getLocalDateString()) {
                          alert(`Check-in is only allowed on the expected visit date (${visitDateStr})`);
                          return;
                        }
                        handleStatusUpdate(record._id, 'checked-in');
                      }}
                      className="vms-mini-action"
                    >
                      Check In
                    </Button>
                  </div>
                )}
              </article>
            );
          })}

          {activityTab === 'pending' && pendingVisits.map((visit, idx) => {
            const visitorName = `${visit.visitor?.firstName || 'Unknown'} ${visit.visitor?.lastName || 'Visitor'}`;
            return (
              <article key={`pending-${visit._id || idx}`} className="vms-activity-card">
                <div className="vms-activity-card-top">
                  <h3 className="flex items-center gap-2">
                    {visit.visitor?.photo ? (
                      <button
                        type="button"
                        onClick={() => openPhotoPreview(visit.visitor.photo, visitorName)}
                        className="w-7 h-7 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700"
                        title="View visitor photo"
                      >
                        <img src={visit.visitor.photo} alt="Visitor" className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-slate-500">
                        {visit.visitor?.firstName?.charAt(0) || 'V'}
                      </span>
                    )}
                    {visitorName}
                  </h3>
                  <span className="info">Pending</span>
                </div>
                <p>{visit.purpose || 'General visit'} is waiting for approval.</p>
                <div className="vms-activity-meta">
                  <span><FiClock /> {formatVisitTime(visit.createdAt)}</span>
                  <strong>{visit.host ? `${visit.host.firstName} ${visit.host.lastName || ''}`.trim() : 'Host'}</strong>
                </div>
                {(isEmployee || user.role?.toLowerCase() === 'admin') && (
                  <div className="vms-card-actions flex gap-2 mt-2">
                    <Button
                      size="small"
                      type="primary"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white border-none text-xs flex-1"
                      onClick={() => handleStatusUpdate(visit._id, 'approved')}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      danger
                      className="text-xs flex-1"
                      onClick={() => handleStatusUpdate(visit._id, 'rejected')}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </article>
            );
          })}

          {activityTab === 'recent' && recentVisits.slice(0, 5).map((visit, idx) => {
            const visitorName = `${visit.visitor?.firstName || 'Unknown'} ${visit.visitor?.lastName || 'Visitor'}`;
            return (
              <article key={`recent-${visit._id || idx}`} className="vms-activity-card">
                <div className="vms-activity-card-top">
                  <h3 className="flex items-center gap-2">
                    {visit.visitor?.photo ? (
                      <button
                        type="button"
                        onClick={() => openPhotoPreview(visit.visitor.photo, visitorName)}
                        className="w-7 h-7 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700"
                        title="View visitor photo"
                      >
                        <img src={visit.visitor.photo} alt="Visitor" className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-slate-500">
                        {visit.visitor?.firstName?.charAt(0) || 'V'}
                      </span>
                    )}
                    {visitorName}
                  </h3>
                  <span className="neutral">Recent</span>
                </div>
                <p>{visit.purpose || 'General visit'} logged in the visitor system.</p>
                <div className="vms-activity-meta">
                  <span><FiClock /> {formatVisitTime(visit.checkInTime || visit.createdAt)}</span>
                  <strong>{visit.status === 'approved' && visit.preApproved ? 'pre-approved' : (visit.status || 'updated')}</strong>
                </div>
              </article>
            );
          })}
        </div>
      </aside>
      <Modal
        open={Boolean(previewPhoto)}
        onCancel={() => setPreviewPhoto('')}
        footer={null}
        title={previewName || 'Visitor Photo'}
      >
        <img src={previewPhoto} alt={previewName || 'Visitor'} className="w-full rounded-lg border border-slate-200 dark:border-slate-700" />
      </Modal>

      <Modal
        open={statModal.isOpen}
        onCancel={() => setStatModal({ ...statModal, isOpen: false })}
        title={statModal.title}
        footer={null}
        width={800}
      >
        <Table
          dataSource={statModalData}
          columns={statModal.type === 'employee' ? [
            { title: 'Name', key: 'name', render: (_, record) => `${record.firstName || ''} ${record.lastName || ''}` },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: 'Phone', dataIndex: 'phoneNumber', key: 'phoneNumber' },
            { title: 'Role', dataIndex: 'role', key: 'role', render: (role) => <span style={{ textTransform: 'capitalize' }}>{role || 'Employee'}</span> }
          ] : [
            { title: 'Pass Code', dataIndex: 'passCode', key: 'passCode' },
            { title: 'Visitor Name', key: 'visitorName', render: (_, record) => `${record.visitor?.firstName || ''} ${record.visitor?.lastName || ''}` },
            { title: 'Host Name', key: 'hostName', render: (_, record) => `${record.host?.firstName || ''} ${record.host?.lastName || ''}` },
            { title: 'Status', dataIndex: 'status', key: 'status', render: (status, record) => <span style={{ textTransform: 'capitalize' }}>{status === 'approved' && record.preApproved ? 'pre-approved' : status}</span> },
            { title: 'Date', key: 'date', render: (_, record) => new Date(record.expectedDate || record.createdAt).toLocaleDateString() }
          ]}
          rowKey="_id"
          loading={statModalLoading}
          pagination={{
            current: statModalPage,
            pageSize: 10,
            total: statModalTotal,
            onChange: (page) => setStatModalPage(page),
          }}
        />
      </Modal>
    </div>
  );
};

export default Dashboard;
